import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Card, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';
import { AppSelectors } from '../../../selectors';
import { useGetGroupablePropertiesData } from '../../../hooks/queries/useGetGroupablePropertiesData';

import { ZRMOverview } from '../ZRMOverview';
import { NoSearchResults } from '../NoSearchResults';
import GroupablePropertiesViewGrid from '../GroupablePropertiesViewGrid';
import GroupablePropertiesViewList from '../GroupablePropertiesViewList';

import type { ListingGroup } from '../../../types/GroupablePropertiesListDto';

export type Props = {
  onRemoveSearchTextClick?: (value: string) => void;
};

const GroupablePropertiesDisplay = ({ onRemoveSearchTextClick }: Props) => {
  const { isScreenLgGTE, user, propertiesFilters, isRunningInProd } = useSelector(
    (state) => ({
      isScreenLgGTE: AppSelectors.makeIsScreenLgGTE(state),
      user: state.user,
      propertiesFilters: state.properties.propertiesFilters,
      isRunningInProd: AppSelectors.makeIsRunningInProd(state),
    }),
    shallowEqual,
  );

  // Fetch groupable properties data using the new v2 API structure
  // This replaces the previous pattern where data was passed as props
  const { data, isPending, isFetching } = useGetGroupablePropertiesData({
    searchText: propertiesFilters.searchText || '',
    subNavFilterType: propertiesFilters.subNavFilterType || 'all',
  });

  const { listingGroups = [], listingCount } = data as {
    listingGroups?: Array<ListingGroup>;
    listingCount?: { total: number; active: number; inactive: number; archived: number };
  };

  // Manage loading state internally (previously passed as props)
  const [didPropertiesLoad, setDidPropertiesLoad] = useState(false);

  const isLoading = isPending || isFetching;
  const showArchived = propertiesFilters.includeArchived;
  const hasAnyArchivedListings = (listingCount?.archived || 0) > 0;

  const handleRemoveSearchTextClick = (value: string) => {
    if (onRemoveSearchTextClick) {
      onRemoveSearchTextClick(value);
    }
  };

  // Transform v2 API response (ListingGroup[]) to ListingGroup[]
  // This preserves the type information needed for proper rendering
  const listings: Array<ListingGroup> = useMemo(() => {
    return listingGroups.map((group) => {
      // Return the group as-is since ListingGroup is the canonical type
      return group;
    });
  }, [listingGroups]);

  // Update loading state when data fetch completes
  useEffect(() => {
    if (!isPending && !isFetching) {
      setDidPropertiesLoad(true);
    }
  }, [isPending, isFetching]);

  const toggleArchivedView = () => {
    // This will be handled by the parent component via propertiesFilters update
    // The component will automatically re-fetch when propertiesFilters change
  };

  const renderTimedOutDisplay = () => (
    <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '1200' }}>
      <Text
        css={{
          textAlign: 'center',
          padding: { base: '0 {spacing.400}', lg: '0' },
          marginBottom: '800',
          marginInline: '400',
        }}
      >
        We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
      </Text>
    </Box>
  );

  const renderNoListingsFound = () => {
    if (propertiesFilters.searchText) {
      return <NoSearchResults showArchived={showArchived} onRemoveSearchTextClick={handleRemoveSearchTextClick} />;
    }

    if (showArchived) {
      return (
        <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
          You don&apos;t have any archived properties.
        </Text>
      );
    }

    if (user.features.canAddListing) {
      return (
        <ZRMOverview hasAnyArchivedListings={hasAnyArchivedListings} handleShowArchivedClick={toggleArchivedView} />
      );
    }

    return (
      <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
        No properties found.
      </Text>
    );
  };

  const renderView = () =>
    isScreenLgGTE ? (
      <GroupablePropertiesViewList listings={listings} />
    ) : (
      <GroupablePropertiesViewGrid listings={listings} />
    );

  const renderListingsDisplay = () => (
    <React.Fragment>
      {listings.length > 0 ? (
        <GroupablePropertiesViewList listings={listings} />
      ) : (
        <Card
          outlined={false}
          elevated={false}
          css={{
            marginBlock: '1200',
            backgroundColor: 'transparent',
            textAlign: 'center',
            lg: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' },
          }}
        >
          {renderNoListingsFound()}
        </Card>
      )}
    </React.Fragment>
  );

  return (
    <Box data-testid="GroupablePropertiesDisplay">
      {!didPropertiesLoad && !isLoading ? renderTimedOutDisplay() : renderListingsDisplay()}
    </Box>
  );
};

export { GroupablePropertiesDisplay };
export default GroupablePropertiesDisplay;
